import { fetchPage, type PageResponse } from './collections';

/**
 * Global search (`AM-132 point 10`, 07 §4.15, `D34`): `GET /search` carries no permission of its
 * own - the prefilter and the authoritative re-verification (`ISO-19.2`, `ISO-19.3`) decide what
 * each actor gets back, the portal included. The console never asks a filtered index for anything
 * a record's own detail endpoint would refuse.
 */

/**
 * The nine values of `search_documents.entity_type` (`ISO-19.1`, `SearchEntityTypes.All` as
 * migration 0022 leaves the `CHECK`), closed list. It is what the type chips of `/search` offer and
 * what the URL accepts; it is **not** what decides which results are displayed. A copy of a
 * server-side list always lags it - this one lagged by `person` and `invoice` for as long as they
 * existed (`AM-144` point 1) - so nothing that renders a page may intersect with it.
 */
export const SEARCH_ENTITY_TYPES = [
  'ticket',
  'request',
  'task',
  'knowledge_article',
  'asset',
  'configuration_item',
  'organization',
  'person',
  'invoice',
] as const;

export type SearchEntityType = (typeof SEARCH_ENTITY_TYPES)[number];

/** Order the palette groups results in (13 §6.1); a type not listed here sorts last, never away. */
export const SEARCH_ENTITY_TYPE_ORDER: readonly SearchEntityType[] = [
  'ticket',
  'request',
  'task',
  'knowledge_article',
  'asset',
  'configuration_item',
  'organization',
  'person',
  'invoice',
];

export interface SearchHit {
  entityType: SearchEntityType;
  entityId: string;
  /** The human number or code, `null` for a type that has none - a person (`AM-144` point 3). */
  reference: string | null;
  title: string;
  snippet?: string | null;
  statusCategory?: string | null;
  organizationId?: string | null;
  updatedAt: string;
  score: number;
}

export interface SearchParams {
  /** 2–200 characters; the endpoint itself enforces the bound (07 §4.15). */
  q: string;
  entityTypes?: readonly SearchEntityType[];
  limit?: number;
  cursor?: string;
}

export const searchApi = {
  search: (params: SearchParams, signal?: AbortSignal): Promise<PageResponse<SearchHit>> =>
    fetchPage<SearchHit>(
      '/api/v1/search',
      {
        limit: params.limit,
        ...(params.cursor === undefined ? {} : { cursor: params.cursor }),
        filters: {
          q: params.q,
          ...(params.entityTypes === undefined || params.entityTypes.length === 0
            ? {}
            : { 'entityType[in]': params.entityTypes.join(',') }),
        },
      },
      signal,
    ),
};
